import _ from 'lodash'
import ComponentState, {
  ARTICLE_ID, GerberaContent, GerberaHeader, IComponentData, IContainerData, ImageData, isContainer,
  isImage, isText, isUrl, ScriptContent, TextData
} from './state'
import findScripts from '../../../misc/find-scripts'

function getComponentById(state: ComponentState, id: string | IComponentData): IComponentData {
  const componentId = _.isString(id) ? id : id.id
  const component = state.data[componentId]
  if (component) {
    return component
  }
  throw new TypeError(`指定されたコンポーネントが見つかりません: id=${id}`)
}

export function componentFinder<T extends IComponentData>(state: ComponentState): (id: string | IComponentData) => T {
  return (id: string | IComponentData) => {
    return <T>getComponentById(state, id)
  }
}

export function containerFinder<T extends IContainerData>(state: ComponentState): (id: string | IComponentData) => T {
  return (id: string | IComponentData) => {
    const component = getComponentById(state, id)
    if (isContainer(component)) {
      return <T>component
    }
    throw new TypeError(`指定されたコンポーネントはコンテナではありません: id=${id}, type=${component.type}`)
  }
}

function findComponent<T extends IComponentData>(state: ComponentState, component: IComponentData, typeFilter: (c: IComponentData) => c is T): T[] {
  if (typeFilter(component)) {
    return [component]
  }
  if (isContainer(component)) {
    return component.childIds
      .map(id => getComponentById(state, id))
      .map(child => findComponent(state, child, typeFilter))
      .reduce((c1, c2) => {
        return c1.concat(c2)
      }, [] as T[])
  }
  return []
}

export function getImages(state: ComponentState): ImageData[] {
  return findComponent(state, getComponentById(state, ARTICLE_ID), isImage)
}

export function getTexts(state: ComponentState): TextData[] {
  return findComponent(state, getComponentById(state, ARTICLE_ID), isText)
}

export function article(state: ComponentState): IContainerData {
  return containerFinder(state)(ARTICLE_ID)
}

export function idGenerator(state: ComponentState): () => string {
  return () => _.uniqueId('c__')
}

export function gerberaContent(state: ComponentState): GerberaContent {
  return state.gerbera
}

export function headerSectionContent(state: ComponentState): GerberaHeader {
  const article = gerberaContent(state).articleJson
  const header = article ? article.header : undefined
  return header || {}
}

export function isContentSaved(state: ComponentState): boolean {
  return state.isContentSaved
}

export function timeStampForSaveState(state: ComponentState): number {
  return state.timeStampForSaveState
}

export function embedScripts(state: ComponentState): ScriptContent[] {
  const scripts = findComponent(state, getComponentById(state, ARTICLE_ID), isUrl)
    .map(url => url.oembedLink.response.html || '')
    .map(html => findScripts(html))
    .filter(script => !!script)
    .reduce((s1, s2) => s1.concat(s2), [] as ScriptContent[])
  return _.uniqWith(scripts, (s1, s2) => {
    return (s1.src === s2.src) && (s1.body === s2.body)
  })
}

export function canUndo(state: ComponentState): boolean {
  return state.history.currentIndex > 0
}

export function canRedo(state: ComponentState): boolean {
  return state.history.currentIndex < state.history.patches.length
}

export default {
  componentFinder,
  containerFinder,
  getImages,
  getTexts,
  article,
  idGenerator,
  gerberaContent,
  headerSectionContent,
  isContentSaved,
  timeStampForSaveState,
  embedScripts,
  canUndo,
  canRedo
}
